import {
  CloseButton,
  Combobox,
  Input,
  InputBase,
  InputBaseProps,
  Loader,
  rem,
  ScrollArea,
  Text,
  useCombobox,
} from '@mantine/core';
import {IconCheck} from '@tabler/icons-react';
import {FC, memo, useCallback, useMemo, useState} from 'react';
import {getEnumSelectOptions} from '../../../api/utils.ts';
import {DropdownOptionItem, Nullish} from '../../../types';
import {ComboboxOption} from './combobox-option.tsx';
import {
  ComboboxInputProps,
  OnOptionSubmit,
  QuickAddServiceCall,
} from './types.ts';
import {ComboboxUtils} from './utils.ts';
import {PrimaryColorCssVar} from '../../../styles/default-theme.tsx';
import {useNotification} from '../../../hooks/use-notifications.tsx';

type SelectInputProps = InputBaseProps &
  CreatableSelectProps & {
    value: Nullish<string>;
    onChange: (value: Nullish<string>) => void;
    clearable?: boolean;
    searchable?: boolean;
  };

type CreatableSelectProps =
  | {
      creatable: true;
      getCreateLabel?: (query: string) => string;
      onCreate: QuickAddServiceCall<unknown>;
    }
  | {
      creatable?: false;
      getCreateLabel?: never;
      onCreate?: never;
    };

export type SelectProps = SelectInputProps & ComboboxInputProps;

const CREATED_OPTION_VALUE = '$__created__$';

export const Select: FC<SelectProps> = (props) => {
  const {
    comboboxOptions,
    comboboxProps,
    onChange,
    disabled,
    readOnly,
    value,
    data,
    enumObject,
    limit,
    maxDropdownHeight = 250,
    clearable,
    loading,
    placeholder = 'Select Value',
    leftSection,
    searchable,
    description,
    error,
    creatable,
    getCreateLabel,
    onCreate,
    ...restInputBaseProps
  } = props;

  const notifications = useNotification();

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
      setIsCreating(false);
      setCreationSuccess(false);
    },

    onDropdownOpen: () => {
      if (searchable) {
        combobox.focusSearchInput();
      }
    },
    ...comboboxOptions,
  });

  const _normalizedOptions = useMemo(
    () =>
      enumObject
        ? getEnumSelectOptions(enumObject)
        : ComboboxUtils.normalizeOptionsData(data),
    [data, enumObject],
  );

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [creationSuccess, setCreationSuccess] = useState(false);

  const onOptionSubmit = useCallback<OnOptionSubmit>(
    async (_value) => {
      if (_value === CREATED_OPTION_VALUE) {
        if (onCreate && search) {
          setIsCreating(true);
          try {
            const response = await onCreate(search);
            setIsCreating(false);

            if (response.hasErrors) {
              notifications.error('Failed to create new option');
              return;
            }

            setCreationSuccess(true);

            setTimeout(() => {
              combobox.closeDropdown();
            }, 1000);

            if (response.data !== null) {
              const normalizedOption = ComboboxUtils.normalizeOptionItem(
                response.data,
              );
              _normalizedOptions.push(normalizedOption);
              onChange(normalizedOption.value);
            }
          } catch (error) {
            notifications.error('Failed to create new option');
            setIsCreating(false);
          }
        }
      } else {
        onChange(_value);
        combobox.closeDropdown();
      }
    },
    [_normalizedOptions, combobox, notifications, onChange, onCreate, search],
  );

  const filteredOptions = useMemo<DropdownOptionItem<string>[]>(() => {
    const options = ComboboxUtils.getFilteredOptions(
      _normalizedOptions,
      search,
      limit,
    );

    if (
      creatable &&
      search &&
      !options.some((opt) => opt.label.toLowerCase() === search.toLowerCase())
    ) {
      return [
        ...options,
        {
          value: CREATED_OPTION_VALUE,
          label: getCreateLabel?.(search) ?? `Create "${search}"`,
          rightSection: isCreating ? (
            <Loader size={rem(14)} />
          ) : creationSuccess ? (
            <IconCheck size={12} color={PrimaryColorCssVar} />
          ) : undefined,
        },
      ];
    }

    return options;
  }, [
    _normalizedOptions,
    limit,
    search,
    creatable,
    getCreateLabel,
    isCreating,
    creationSuccess,
  ]);

  const onClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const selectedOption = useMemo(() => {
    return _normalizedOptions.find((x) => x.value === value);
  }, [_normalizedOptions, value]);

  const openDropdown = useCallback(() => {
    if (readOnly) return;
    combobox.openDropdown();
  }, [combobox, readOnly]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={onOptionSubmit}
      withinPortal={false}
      keepMounted={false}
      {...comboboxProps}
      disabled={disabled}
      readOnly={readOnly}
    >
      <Combobox.Target>
        <InputBase
          error={error}
          disabled={disabled}
          component='button'
          type='button'
          pointer={!disabled && !readOnly}
          leftSection={leftSection}
          rightSection={
            <RightSection
              clearable={clearable}
              onClear={onClear}
              loading={loading}
              optionSelected={Boolean(selectedOption)}
            />
          }
          onClick={openDropdown}
          onFocus={openDropdown}
          rightSectionPointerEvents={clearable && value ? 'all' : 'none'}
          description={description}
          {...restInputBaseProps}
        >
          {selectedOption?.label ? (
            <Text truncate='end' fz='sm'>
              {selectedOption.label}
            </Text>
          ) : (
            <Input.Placeholder>{placeholder}</Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown onBlur={() => combobox.closeDropdown()}>
        {(searchable || creatable) && (
          <Combobox.Search
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder='Search...'
          />
        )}
        <Combobox.Options>
          <ScrollArea.Autosize type='scroll' mah={maxDropdownHeight}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((item, index) => (
                <ComboboxOption
                  key={`option${index}-${item.value}`}
                  active={value === item.value}
                  {...item}
                />
              ))
            ) : (
              <Combobox.Empty>No Results</Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

type RightSectionProps = {
  clearable?: boolean;
  onClear: () => void;
  loading?: boolean;
  optionSelected: boolean;
};
const RightSection = memo<RightSectionProps>(
  ({loading, clearable, onClear, optionSelected}) => {
    return loading ? (
      <Loader size={'xs'} />
    ) : clearable && optionSelected ? (
      <CloseButton
        onClick={onClear}
        size='sm'
        onMouseDown={(event) => event.preventDefault()}
        aria-label='Clear value'
      />
    ) : (
      <Combobox.Chevron />
    );
  },
);